import React from "react";
import { Box, Card, CardContent, CardHeader, IconButton, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import Stack from "@mui/material/Stack";
import { Edit } from "@mui/icons-material";
import { HtmlTooltip, IconContainer, StyledStack } from "../StyledComponents/styled";

// Recursively flattens React.Fragment wrappers so conditional-fragment children
// (e.g. {cond && <><A /><B /></>}) become a flat list of real elements.
const flattenChildren = (children) => {
  const result = [];
  React.Children.forEach(children, (child) => {
    if (child === null || child === undefined || child === false || child === true) return;
    if (React.isValidElement(child) && child.type === React.Fragment) {
      result.push(...flattenChildren(child.props.children));
      return;
    }
    result.push(child);
  });
  return result;
};

/**
 * This component is designed to create flexible and reusable card layouts
 * for rendering content in various React views. It consists of multiple components:
 *
 * - `Cards`: The main container for organizing card content.
 * - `SmallCardContent`: A higher-order component (HOC) for small card sections, suitable for compact information.
 * - `MediumCardContent`: Another HOC for medium-sized cards, ideal for displaying more extensive content.
 */

const styles = {
  mainBody: {
    flexGrow: "1",
    minHeight: "100%",
    display: "flex",
    flexDirection: "column",
    padding: "88px 16px",
  },
  gridItem: {
    display: "flex",
    flexDirection: "column",
    background: "rgba(240, 75, 85, 0.05)",
    paddingBottom: "40px",
    borderRadius: " 16px",
    textAlign: "left",
    color: "rgb(0, 75, 80)",
  },
  stackItem: {
    marginBottom: "0px",
    padding: "0px 16px",
  },
  stackText: {
    margin: "0px",
    fontWeight: "600",
    lineHeight: "1.57143",
    fontSize: "0.875rem",
    opacity: "0.64",
  },
};

const Cards = (props) => {
  const { children, ...other } = props;
  const items = flattenChildren(children);
  const left = items.filter((child) => child.props?.column !== "right");
  const right = items.filter((child) => child.props?.column === "right");

  // Two independent stacks with the same gap — tiles do not stretch to a
  // shared row height, so spacing stays even when one column is taller.
  const columnSx = {
    width: "50%",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  };

  return (
    <Box
      {...other}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 2,
        width: "100%",
        ...(other.sx || {}),
      }}
    >
      <Box sx={columnSx}>{left}</Box>
      <Box sx={columnSx}>{right}</Box>
    </Box>
  );
};

export const SmallCardContent = (props) => {
  const { children, showEdit, editAction, editIcon, cardIcon, editTooltipText, column, ...other } = props;
  return (
    <Box {...other} sx={{ width: "100%", minWidth: 0, ...(other.sx || {}) }}>
      <Stack spacing={1} style={styles.gridItem}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: 48 }}>
          <div style={styles.stackItem}>{cardIcon}</div>
          {showEdit && (
            <StyledStack direction="row" spacing={2}>
              <HtmlTooltip title={"Edit details"}>
                <IconContainer sx={{ pt: "12px", marginRight: "8px" }}>
                  <a href="#edit" onClick={editAction} aria-label={editTooltipText}>
                    <Edit sx={{ color: "#AF3241" }} />
                  </a>
                </IconContainer>
              </HtmlTooltip>
            </StyledStack>
          )}
        </div>

        {/* -------- main content for the card --------- */}
        <Box>{children}</Box>
      </Stack>
    </Box>
  );
};

export const MediumCardContent = (props) => {
  const { children, showHeaderText, headerText, ...other } = props;
  return (
    <Grid item xs={4} {...other}>
      <Card sx={{ height: "60vh", width: "80vh" }} style={styles.card}>
        {/* ------ header text for card ------ */}
        {showHeaderText && (
          <CardHeader
            title={
              <>
                <Typography variant="h6"> {headerText} </Typography>
              </>
            }
          />
        )}
        {/* --------- card content ------- */}
        <CardContent>{children}</CardContent>
      </Card>
    </Grid>
  );
};

export default Cards;
